import { AvatarGroupV2, AvatarV2 } from '@cimplico/components';
import { useState } from 'react';
import { WorksheetBase, WorksheetStatusEnum, ReconciliationWorksheet } from '../../../types/worksheet';
import UpdateAssigneesDialog from '../../worksheet/UpdateAssigneesDialog';
import { formatMixedCase } from '../../../utilities/formatting';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { SubscriptionTier } from '../../../types/firm';
import { useUpdateWorksheets } from '../../../queries/use-update-worksheets';
import { toastNotification } from '../../../components/toast/Toast';
import UpgradePrompt from '../../../components/upgrade-prompt/UpgradePrompt';
import { useHasFeatureAccess } from '../../../hooks/use-has-feature-access';

type AssignedProps = {
    worksheet: WorksheetBase | ReconciliationWorksheet;
    size?: 'mini' | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | 'jumbo';
    readOnly?: boolean;
};
function Assigned({ worksheet, size = 'xs', readOnly = false }: AssignedProps): JSX.Element {
    const [action, setAction] = useState<string | undefined>();
    const updateWorksheetQuery = useUpdateWorksheets();
    const featureAccess = { canUpdateAssignees: useHasFeatureAccess('worksheet-update-assignees:write') };

    const { readOnly: workpaperReadOnly } = useWorkpaperContext();

    const disableActions = workpaperReadOnly || readOnly;

    function onAdd(): void {
        if (!featureAccess.canUpdateAssignees) {
            setAction('UPGRADE');
        } else {
            setAction('UPDATE_USERS');
        }
    }

    function removeAssignees(toRemoveId: string): void {
        if (!featureAccess.canUpdateAssignees) {
            setAction('UPGRADE');
            return;
        }

        const toRemoveUser = worksheet.assigned.find((a) => a.id === toRemoveId);

        if (!toRemoveUser) {
            return;
        }

        updateWorksheetQuery.mutate(
            [{ id: worksheet.id, fields: { assigned: worksheet.assigned.map((a) => a.id).filter((id) => id !== toRemoveId) } }],
            {
                onSuccess: () => {
                    toastNotification('success', `Unassigned ${toRemoveUser.firstName} ${toRemoveUser.lastName}!`);
                },
                onError: (err) => {
                    toastNotification('error', `Unable to unassign users!`, err.response?.data.results[0].error.fields.errorMessage);
                },
            }
        );
    }

    return (
        <>
            {action === 'UPGRADE' && <UpgradePrompt onClose={(): void => setAction(undefined)} targetTier={SubscriptionTier.ESSENTIAL} />}
            {action === 'UPDATE_USERS' && (
                <UpdateAssigneesDialog worksheet={worksheet as ReconciliationWorksheet} onClose={(): void => setAction(undefined)} />
            )}

            <AvatarGroupV2 size={size} onAdd={!disableActions && worksheet.status !== WorksheetStatusEnum.EXCLUDED ? onAdd : undefined}>
                {worksheet.assigned.map((user) => {
                    return (
                        <AvatarV2
                            key={user.id}
                            color={user.profileHexColour}
                            src={user.avatarUrl}
                            alt={`${user.firstName[0]}${user.lastName[0]}`}
                            tooltip={`${user.firstName} ${user.lastName} ${user.role ? `(${formatMixedCase(user.role)})` : ''}`}
                            onRemove={
                                !disableActions && worksheet.status !== WorksheetStatusEnum.EXCLUDED
                                    ? (): void => removeAssignees(user.id)
                                    : undefined
                            }
                        />
                    );
                })}
            </AvatarGroupV2>
        </>
    );
}

export default Assigned;
